import { useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { MyProfileCard } from "../ProfileScreen/MyProfileCard.tsx";
import { OutlinedButton } from "../../UI/Button/OutlinedButton.tsx";
import { PrimaryButton } from "../../UI/Button/PrimaryButton.tsx";
import { Modal } from "../../UI/Modal/Modal.tsx";
import { useWithLoading } from "../../UI/Loading/useWithLoading.ts";
import {
  MyWithdrawalRight,
  useMyWithdrawalRights,
} from "./useMyWithdrawalRights.ts";
import { useRequestWithdrawal } from "./useRequestWithdrawal.ts";

const PROCESSING_WINDOW_IN_DAYS = 14;

function WithdrawalRightRow({
  right,
  onRequested,
}: {
  right: MyWithdrawalRight;
  onRequested: () => Promise<unknown>;
}) {
  const { t } = useTranslation("withdrawal");
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { requestWithdrawal } = useRequestWithdrawal();
  const { withLoading, loading } = useWithLoading();

  const confirmAndRequest = async () => {
    setError(null);
    try {
      await withLoading(() => requestWithdrawal(right.purchaseKey));
    } catch {
      setError(t("errorText"));
      return;
    }
    setConfirming(false);
    await onRequested().catch(() => undefined);
  };

  const closeConfirmation = () => {
    setError(null);
    setConfirming(false);
  };

  return (
    <div className={"space-y-3"}>
      {right.request ? (
        <div
          className={
            "bg-green-50 border border-green-200 rounded-2xl p-4 text-sm text-green-900"
          }
        >
          <p className={"font-semibold"}>{t("requestedTitle")}</p>
          <p>
            {t("requestedDescription", {
              date: dayjs(right.request.requestedAtUtc).format("DD/MM/YYYY"),
            })}
          </p>
          <p className={"mt-2"}>
            {t("refundDescription", {
              date: dayjs(right.request.requestedAtUtc)
                .add(PROCESSING_WINDOW_IN_DAYS, "day")
                .format("DD/MM/YYYY"),
            })}
          </p>
        </div>
      ) : (
        <>
          <p className={"text-sm text-gray-600"}>
            {t("description", {
              date: dayjs(right.withdrawalDeadlineUtc).format("DD/MM/YYYY"),
            })}
          </p>
          <OutlinedButton
            label={t("ctaText")}
            disabled={!right.canRequest}
            onClick={() => setConfirming(true)}
          />
        </>
      )}

      <Modal
        isOpen={confirming}
        onClose={closeConfirmation}
        title={t("confirmTitle")}
      >
        <div className={"space-y-4"}>
          <p className={"text-sm text-gray-600"}>{t("confirmDescription")}</p>
          {error ? (
            <p
              className={
                "text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3"
              }
            >
              {error}
            </p>
          ) : null}
          <div className={"flex gap-3"}>
            <PrimaryButton
              label={t("confirmCta")}
              loading={loading}
              disabled={loading}
              onClick={confirmAndRequest}
            />
            <OutlinedButton
              label={t("cancelCta")}
              disabled={loading}
              onClick={closeConfirmation}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}

export function WithdrawalCard() {
  const { t } = useTranslation("withdrawal");
  const { withdrawalRights, loading, refetch } = useMyWithdrawalRights();

  if (loading || withdrawalRights.length === 0) return null;

  return (
    <MyProfileCard>
      <h2 className={"font-semibold text-lg"}>{t("title")}</h2>
      <div className={"space-y-6"}>
        {withdrawalRights.map((right) => (
          <WithdrawalRightRow
            key={right.purchaseKey ?? right.purchasedAtUtc}
            right={right}
            onRequested={refetch}
          />
        ))}
      </div>
    </MyProfileCard>
  );
}
